import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { MetadataService } from '../services/metadata.service';
import { GlobalErrorService } from '../services/global-error.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private metadataService: MetadataService,
    private globalErrorService: GlobalErrorService,
    private router: Router
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    console.log('AuthGuard: Checking authentication for route:', state.url);
    
    try {
      // First, check if we have a code parameter and handle it
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code) {
        console.log('AuthGuard: Found authentication code, attempting token exchange...');
        
        // Handle the code exchange first
        const authResult = await this.authService.handleAuthentication();
        
        if (authResult) {
          console.log('AuthGuard: Code exchange successful, proceeding with metadata verification...');
          // Now verify authentication using the metadata endpoint
          const metadataResult = await this.metadataService.getMetadataWithDetails();
          
          if (metadataResult.success) {
            console.log('AuthGuard: Metadata verification successful, allowing navigation');
            // Clear any previous metadata errors
            this.globalErrorService.clearMetadataError();
            
            // Store any warning errors (like 403) for display on pages
            if (metadataResult.error && metadataResult.statusCode === 403) {
              this.globalErrorService.setMetadataError(metadataResult.error, metadataResult.details || '');
            }
            return true;
          } else {
            console.log('AuthGuard: Metadata verification failed after code exchange');
            // Store the error for display on pages
            this.globalErrorService.setMetadataError(
              metadataResult.error || 'Authentication failed', 
              metadataResult.details || ''
            );
            return false;
          }
        }
        
        console.log('AuthGuard: Code exchange failed, blocking navigation');
        return false;
      }
      
      // No code parameter, verify authentication using metadata endpoint
      console.log('AuthGuard: No code parameter, verifying authentication with metadata endpoint...');
      const metadataResult = await this.metadataService.getMetadataWithDetails();
      
      if (metadataResult.success) {
        console.log('AuthGuard: Metadata verification successful, allowing navigation');
        // Clear any previous metadata errors
        this.globalErrorService.clearMetadataError();
        
        // Store any warning errors (like 403) for display on pages
        if (metadataResult.error && metadataResult.statusCode === 403) {
          this.globalErrorService.setMetadataError(metadataResult.error, metadataResult.details || '');
        }
        return true;
      }

      // Metadata call failed (likely 401), redirect to SSO
      console.log('AuthGuard: Metadata verification failed, redirecting to SSO...');
      
      // Store the error for display on pages (if it's not a 401)
      if (metadataResult.statusCode !== 401) {
        this.globalErrorService.setMetadataError(
          metadataResult.error || 'Authentication failed', 
          metadataResult.details || ''
        );
      }
      
      try {
        await this.authService.redirectToSSO();
        console.log('AuthGuard: SSO redirect initiated successfully');
      } catch (redirectError) {
        console.error('AuthGuard: Failed to initiate SSO redirect:', redirectError);
      }
      return false;
    } catch (error) {
      console.error('AuthGuard: Error during authentication check:', error);
      return false;
    }
  }
}
